import type { MessageKey } from '@freightapi/i18n'
import { Button, EmptyState, formatDateTime } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useSignI18n } from '../lib/locale'
import { discardItem, retryItem, useSubmissionQueue } from '../lib/offline-queue'
import { shortHash } from '../lib/queue-hash'
import type { QueueItem } from '../lib/queue-store'

/**
 * The queue screen of M2-08: what this phone is still carrying, and why.
 *
 * It is the one route that is not code split. Every other screen may fetch its chunk when it is
 * opened, because every other screen is opened with a connection; this one is opened *because*
 * there is none, and a screen about offline work that has to be downloaded first is no screen at
 * all. The empty `codeSplitGroupings` keeps it in the entry bundle.
 */
export const Route = createFileRoute('/queue')({
  component: QueueScreen,
  codeSplitGroupings: [],
})

/**
 * Everything that was captured but has not reached the API yet.
 *
 * A driver who signed in a tunnel is owed more than a spinner: what was recorded, when, what its
 * content hash is — so the same number can be read back off the shipment afterwards — and, when
 * something stopped, which of the two kinds of stopping it was. A link somebody else already used
 * is not a failure and is not offered a retry button; a refusal is, and so is a dropped connection
 * that ran out of attempts.
 */
function QueueScreen() {
  const { t } = useSignI18n()
  const { items, ready } = useSubmissionQueue()

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold text-display text-ink">{t('queue.title')}</h1>
        <p className="text-body text-muted">{t('queue.body')}</p>
      </div>

      {ready && items.length === 0 ? (
        <EmptyState title={t('queue.empty.title')} description={t('queue.empty.body')} />
      ) : (
        <ul data-queue-list="" className="flex flex-col gap-3">
          {items.map((item) => (
            <QueueRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  )
}

function QueueRow({ item }: { readonly item: QueueItem }) {
  const { t, locale } = useSignI18n()
  const stopped = item.status === 'conflict' || item.status === 'failed'

  return (
    <li
      data-queue-item={item.kind}
      data-queue-status={item.status}
      className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-body text-ink">{t(`queue.kind.${item.kind}`)}</span>
        <span
          className={
            item.status === 'failed' || item.status === 'conflict'
              ? 'text-caption text-danger'
              : 'text-caption text-muted'
          }
        >
          {t(`queue.state.${item.status}`)}
        </span>
      </div>

      <p className="text-caption text-muted">
        {t('queue.captured', { timestamp: formatDateTime(item.createdAt, locale) })}
      </p>

      {/* The proof the content did not change between the ramp and the tunnel exit (M2-08). */}
      <p data-queue-hash={item.hash} className="font-mono text-caption text-muted">
        {t('queue.hash', { hash: shortHash(item.hash) })}
      </p>

      {item.attempts === 0 ? null : (
        <p className="text-caption text-muted">{t('queue.attempts', { count: item.attempts })}</p>
      )}

      {item.error === undefined ? null : (
        <p data-queue-reason={item.error.code} className="text-caption text-ink">
          {t(reasonKey(item.error.code), { code: item.error.code })}
        </p>
      )}

      {stopped ? (
        <div className="flex flex-wrap gap-2">
          {item.status === 'failed' ? (
            <Button variant="ghost" onClick={() => void retryItem(item.id)}>
              {t('queue.retry')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => void discardItem(item.id)}>
            {t('queue.discard')}
          </Button>
        </div>
      ) : null}
    </li>
  )
}

/**
 * The sentence behind an error code. Codes without one fall through to a line that names the code
 * itself: a driver cannot act on `idempotency_key_reused`, but a dispatcher on the phone can read
 * it out, which beats a generic apology.
 */
const REASONS: Partial<Record<string, MessageKey<'sign'>>> = {
  queue_blocked: 'queue.reason.queue_blocked',
  hash_mismatch: 'queue.reason.hash_mismatch',
  already_signed: 'queue.reason.already_signed',
  conflict: 'queue.reason.already_signed',
  link_expired: 'queue.reason.link_expired',
  token_expired: 'queue.reason.link_expired',
  token_rotated: 'queue.reason.link_expired',
  network_error: 'queue.reason.network',
}

function reasonKey(code: string): MessageKey<'sign'> {
  return REASONS[code] ?? 'queue.reason.refused'
}
